import React, { FC, memo, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { Box } from 'entities/Box';
import { ContractInfo } from 'entities/Contract';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatVatRate, getVatRateRow } from 'shared/utils/helpers/formatVatRate';

interface PriceDetailsTableProps {
  contractInfo: ContractInfo;
  boxInfo: Nullable<Box>;
}

export const PriceDetailsTable: FC<PriceDetailsTableProps> = memo((props) => {
  const { contractInfo } = props;

  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  const priceDetailsBullets = useMemo(() => {
    const tableRows: TableRow[] = [];

    const firstServicePeriodUnitPrice = contractInfo.contractPeriodsUnitPrices[0];
    const secondServicePeriodUnitPrice = contractInfo.contractPeriodsUnitPrices[1];

    if (firstServicePeriodUnitPrice) {
      tableRows.push({
        label: t('1st {{invoiceFrequencyType}} rent', { invoiceFrequencyType: t(contractInfo.invoiceFrequencyType) }),
        value: `${formatPrice(roundNumber(firstServicePeriodUnitPrice.rate))} ${currencySymbol}`,
      });
    }

    if (secondServicePeriodUnitPrice && contractInfo.payForEntirePeriod) {
      tableRows.push({
        label: t('2nd {{invoiceFrequencyType}} rent', { invoiceFrequencyType: t(contractInfo.invoiceFrequencyType) }),
        value: `${formatPrice(roundNumber(secondServicePeriodUnitPrice.rate))} ${currencySymbol}`,
      });
    }

    if (contractInfo.vatRatePercent !== contractInfo.insuranceVatRatePercent) {
      tableRows.push(
        getVatRateRow(t, contractInfo.vatRatePercent, roundNumber(contractInfo.vatRateAmount + contractInfo.insuranceVatRateAmount)),
      );
    }

    if (contractInfo.payForEntirePeriod) {
      tableRows.push({
        label: t('Insurance for {{contractDuration}} {{invoiceFrequencyType}}', {
          contractDuration: contractInfo.contractDuration,
          invoiceFrequencyType: t(`${contractInfo.invoiceFrequencyType}s`),
        }),
        value: `${formatPrice(roundNumber(contractInfo.insuranceAmountWithoutVat))} ${currencySymbol}`,
      });
    } else {
      tableRows.push({
        label: t('Insurance for 1st {{invoiceFrequencyType}}', {
          invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
        }),
        value: `${formatPrice(roundNumber(contractInfo.insuranceAmountWithoutVat))} ${currencySymbol}`,
      });
    }

    if (contractInfo.vatRatePercent !== contractInfo.insuranceVatRatePercent) {
      tableRows.push(
        getVatRateRow(
          t,
          contractInfo.insuranceVatRatePercent,
          `${formatPrice(roundNumber(contractInfo.insuranceVatRateAmount))} ${currencySymbol}`,
          'Insurance VAT ({{vatRatePercent}}%)',
          'Insurance VAT',
        ),
      );
    }

    tableRows.push({
      label: t('Deposit (one time)'),
      value: contractInfo.depositAmount ? `${formatPrice(contractInfo.depositAmount)} ${currencySymbol}` : t('No deposit'),
    });

    const totalWithoutVat = roundNumber(contractInfo.totalToPay - contractInfo.vatRateAmount - contractInfo.insuranceVatRateAmount);
    const totalWithVat = contractInfo.totalToPay;

    if (totalWithoutVat === totalWithVat) {
      tableRows.push({
        label: t('VAT'),
        value: formatVatRate(0, t),
      });

      if (contractInfo.balanceToChargeAmount) {
        tableRows.push({
          label: t(
            `Total for ${
              contractInfo.payForEntirePeriod ? '{{contractDuration}} {{invoiceFrequencyTypes}}' : '1st {{invoiceFrequencyType}}'
            }`,
            {
              contractDuration: contractInfo.contractDuration,
              invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
              invoiceFrequencyTypes: t(`${contractInfo.invoiceFrequencyType}s`),
            },
          ),
          value: `${formatPrice(totalWithVat)} ${currencySymbol}`,
        });

        tableRows.push({
          label: t('From user balance'),
          value: `- ${formatPrice(contractInfo.balanceToChargeAmount)} ${currencySymbol}`,
          color: 'text-accent',
          highlighted: true,
        });

        tableRows.push({
          label: t('Total to pay'),
          value: `${formatPrice(contractInfo.totalToCharge)} ${currencySymbol}`,
          highlighted: true,
        });
      } else {
        tableRows.push({
          label: t(
            `Total to pay (for ${
              contractInfo.payForEntirePeriod ? '{{contractDuration}} {{invoiceFrequencyTypes}}' : '1st {{invoiceFrequencyType}}'
            })`,
            {
              contractDuration: contractInfo.contractDuration,
              invoiceFrequencyType: t(contractInfo.invoiceFrequencyType),
              invoiceFrequencyTypes: t(`${contractInfo.invoiceFrequencyType}s`),
            },
          ),
          value: `${formatPrice(totalWithVat)} ${currencySymbol}`,
          highlighted: true,
        });
      }
    } else {
      if (contractInfo.vatRatePercent === contractInfo.insuranceVatRatePercent) {
        tableRows.push(
          getVatRateRow(t, contractInfo.vatRatePercent, roundNumber(contractInfo.vatRateAmount + contractInfo.insuranceVatRateAmount)),
        );
      } else {
        const vatRow = getVatRateRow(
          t,
          contractInfo.vatRatePercent,
          `${formatPrice(roundNumber(contractInfo.vatRateAmount))} ${currencySymbol}`,
        );
        const isVatAlreadyDisplayed = tableRows.find((row) => row.label === vatRow.label);

        if (!isVatAlreadyDisplayed) {
          tableRows.push(vatRow);
        }
      }

      if (contractInfo.balanceToChargeAmount) {
        tableRows.push({
          label: t('Subtotal'),
          value: `${formatPrice(totalWithVat)} ${currencySymbol}`,
        });

        tableRows.push({
          label: t('From user balance'),
          value: `- ${formatPrice(contractInfo.balanceToChargeAmount)} ${currencySymbol}`,
          color: 'text-accent',
          highlighted: true,
        });

        tableRows.push({
          label: t('Total to pay'),
          value: `${formatPrice(contractInfo.totalToCharge)} ${currencySymbol}`,
          highlighted: true,
        });
      } else {
        tableRows.push({
          label: t('Total to pay'),
          value: `${formatPrice(totalWithVat)} ${currencySymbol}`,
          highlighted: true,
        });
      }
    }

    return tableRows;
  }, [
    contractInfo.contractPeriodsUnitPrices,
    contractInfo.payForEntirePeriod,
    contractInfo.depositAmount,
    contractInfo.totalToPay,
    contractInfo.vatRateAmount,
    contractInfo.insuranceVatRateAmount,
    contractInfo.invoiceFrequencyType,
    contractInfo.contractDuration,
    contractInfo.insuranceAmountWithoutVat,
    contractInfo.balanceToChargeAmount,
    contractInfo.totalToCharge,
    contractInfo.vatRatePercent,
    contractInfo.insuranceVatRatePercent,
    t,
    currencySymbol,
  ]);

  return (
    <div className="flex flex-col bg-secondaryLight rounded-lg">
      <BulletsTable heading={t('Price details')} rows={priceDetailsBullets} labelClassName="max-w-[220px]" />
    </div>
  );
});
